import {
  Badge,
  Box,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  Icon,
  Image,
  Input,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import React from 'react'
import { Button } from '../../../../../../components/Button'
import { FaRegArrowAltCircleRight } from 'react-icons/fa'
import { AiOutlineArrowLeft } from 'react-icons/ai'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { api } from '../../../../../../services/api'
import { useMutation, useQueryClient } from 'react-query'
import { InputGroup, InputLeftAddon } from '@chakra-ui/react'
import { user } from '../../../../../../helpers/user'

const schema = z.object({
  link: z.string().nonempty('Campo obrigatório'),
  message: z.string().nonempty('Campo obrigatório'),
  vetorizacao: z.boolean().optional(),
  repeticao: z.boolean().optional(),
  pedido_repeticao: z.string().optional(),
}).refine((data) => {
  if (data.repeticao && (!data.pedido_repeticao || data.pedido_repeticao.trim() === '')) {
    return false;
  }
  return true;
}, {
  message: 'Necessário preencher o número do pedido de repetição',
  path: ['pedido_repeticao'],
})

async function uploadNovoLayout({
  referencia_produto,
  impressao_produto,
  cor_produto,
  prazo,
  sequencia,
  codigo_produto,
  observacao,
  batidas,
  vendedor,
  numero_nota,
  numero_pedido,
  codigo_parceiro,
  link,
  vetorizacao,
}) {
  const formData = new FormData()

  formData.append('referencia_produto', referencia_produto)
  formData.append('impressao_produto', impressao_produto)
  formData.append('cor_produto', cor_produto)
  formData.append('prazo', prazo)
  formData.append('sequencia', sequencia)
  formData.append('codigo_produto', codigo_produto)
  formData.append('observacao', observacao)
  formData.append('batidas', batidas)
  formData.append('vendedor', vendedor)
  formData.append('numero_nota', numero_nota)
  formData.append('numero_pedido', numero_pedido)
  formData.append('codigo_parceiro', codigo_parceiro)
  formData.append('link', link)
  formData.append('codigo_usuario', user?.codigo_usuario)
  formData.append('vetorizacao', vetorizacao)

  const response = await api.post('/site/v2/orcamento-crm/envia-logo-layout', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return response.data
}

async function updateRepeticaoLayout({
  numero_nota,
  codigo_produto,
  observacao_pedido,
}) {
  const response = await api.put('/site/v2/orcamento-crm/atualiza-repeticao-layout', {
    numero_nota,
    codigo_produto,
    observacao_pedido,
  })

  return response.data
}

export function ModalLayout({ item, nome_vendedor, num_proposta, codigo_parceiro }) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const btnRef = React.useRef()
  const [repeticaoMarcada, setRepeticaoMarcada] = React.useState(false)

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
  })

  const repeticao = watch('repeticao')

  React.useEffect(() => {
    if (repeticao && !repeticaoMarcada) {
      setRepeticaoMarcada(true)
    }
  }, [repeticao, repeticaoMarcada])

  const handleClose = () => {
    const formData = watch()
    
    if (formData.repeticao && (!formData.pedido_repeticao || formData.pedido_repeticao.trim() === '')) {
      toast({
        title: 'Atenção!',
        description: 'Necessário preencher o número do pedido de repetição',
        status: 'warning',
      })
      return
    }
    
    onClose()
  }

  const queryClient = useQueryClient()

  const toast = useToast({
    position: 'top-right',
    duration: 5000,
    isClosable: true,
  })

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: uploadNovoLayout,
    onSuccess: (data) => {
      queryClient.invalidateQueries('infoListLayout')

      reset()

      toast({
        title: 'Layout solicitado com sucesso!',
        description: 'Seu layout foi solicitado com sucesso!',
        status: 'success',
      })
    },
    onError: (error) => {
      toast({
        title: 'Erro ao solicitar layout!',
        description: error.response.data.erros.map((error) => error),
        status: 'error',
      })
    },
  })

  const { mutateAsync: updateRepeticao } = useMutation({
    mutationFn: updateRepeticaoLayout,
    onSuccess: (data) => {
      toast({
        title: 'Repetição marcada com sucesso!',
        description: 'A repetição foi marcada com sucesso!',
        status: 'success',
      })
    },
    onError: (error) => {
      toast({
        title: 'Erro ao marcar repetição!',
        description: error.response?.data?.erros?.map((error) => error) || 'Erro desconhecido',
        status: 'error',
      })
    },
  })

  async function onSubmit(data) {
    // Se a repetição foi marcada, primeiro atualizamos a repetição
    if (data.repeticao) {
      try {
        await updateRepeticao({
          numero_nota: item.numero_nota,
          codigo_produto: item.codigo_produto,
          observacao_pedido: data.pedido_repeticao,
        })
      } catch (error) {
        return // Se der erro na repetição, não continua com o layout
      }
    }

    const params = {
      referencia_produto: item.referencia_produto,
      impressao_produto: item.nome_personalizacao,
      cor_produto: item.descricao_cor,
      prazo: item.prazo,
      sequencia: item.sequencia,
      codigo_produto: item.codigo_produto,
      observacao: data.message,
      batidas: item.qtd_batida,
      vendedor: nome_vendedor,
      numero_nota: item.numero_nota,
      numero_pedido: num_proposta,
      codigo_parceiro: codigo_parceiro,
      link: data.link,
      vetorizacao: data.vetorizacao,
    }

    await mutateAsync(params)
  }

  return (
    <>
      <Button
        bgColor="transparent"
        fontSize="13px"
        size="sm"
        color="#2D2D2D"
        onClick={onOpen}
        _hover={{ filter: 'brightness(0.9)', bgColor: '#d3d3d3' }}
        rightIcon={<Icon as={FaRegArrowAltCircleRight} color="#378BBF" fontSize="19px" />}
      >
        Solicitação
        <Text as="span" color="#378BBF" fontWeight="semibold" ml={1}>
          Layout
        </Text>
      </Button>

      <Drawer isOpen={isOpen} onClose={handleClose} finalFocusRef={btnRef} placement="bottom">
        <DrawerOverlay />
        <DrawerContent minH="100vh">
          <DrawerHeader bgColor="#378BBF" textAlign="center" color="#F5F5F5" textTransform="uppercase">
            LAYOUT
          </DrawerHeader>

          <DrawerBody w="100%" mx="auto" display="flex" flexDir="column" alignItems="center" gap={4}>
            <Box p={0} border="1px solid #CFCFCF">
              <Flex flexDir="column" w="full" alignItems="flex-start">
                <Flex alignItems="center" w="full" justifyContent="space-between" borderBottom="1px solid #CFCFCF">
                  <Flex alignItems="center" px="23px" py="9px" fontWeight="semibold" fontSize="15px">
                    <Text as="span" color="#378BBF">
                      Item 1
                    </Text>
                    <Text color="#2D2D2D" as="span">
                      | {item.nome_produto} | {item.codigo_produto}
                    </Text>

                    {item.trava_item && (
                      <Badge ml={10} colorScheme="orange" variant="solid">
                        O Item precisa ser Editado.
                      </Badge>
                    )}
                  </Flex>
                </Flex>
              </Flex>
              <Grid
                px="23px"
                py="20px"
                fontSize="13px"
                alignItems="center"
                justifyContent="space-between"
                width="full"
                gridTemplateColumns="200px 302px 196px 275px 1fr"
              >
                <Flex gap={'14px'} alignItems="center" borderRight="1px solid #CFCFCF">
                  <Flex alignItems="center" justifyContent="center" w="55px" h="76px" border="1px solid #CFCFCF" borderRadius="10px">
                    <Image src={item.imagens_produto[0].url} objectFit="contain" alt="item" boxSize="63px" />
                  </Flex>

                  <Flex flexDir="column" alignSelf="flex-start" py="9px">
                    <Text isTruncated maxW="120px">
                      <strong>Cor:</strong> {item.descricao_cor}
                    </Text>
                    <Text>
                      <strong>Impressão:</strong> {item.qtd_batida}
                    </Text>
                  </Flex>
                </Flex>

                <Flex borderRight="1px solid #CFCFCF" px={'27px'} py="9px" alignItems="start" h="100%" flexDir="column">
                  <Text>
                    <b>Personalização:</b> {item.nome_personalizacao}
                  </Text>
                  <Text>
                    <b>Prazo:</b> {item.prazo} dias úteis
                  </Text>
                  <Text>
                    <b>Quantidade:</b> {item.quantidade}
                  </Text>
                </Flex>

                <Flex borderRight="1px solid #CFCFCF" px={'27px'} py="9px" alignItems="start" h="100%" flexDir="column">
                  <Text>
                    <b>Preço base:</b> {item.preco_base}
                  </Text>
                  <Text>
                    <b>Preço site:</b> {item.preco_site}
                  </Text>
                </Flex>
                <Flex borderRight="1px solid #CFCFCF" px={'27px'} py="9px" alignItems="start" h="100%" flexDir="column">
                  <Text>
                    <b>Substituição tributária:</b> {item.vlr_substituicao}
                  </Text>
                  <Text>
                    <b>Preço venda:</b> {item.preco_venda}
                  </Text>
                </Flex>
                <Flex px={'27px'} py="9px" alignItems="start" h="100%" flexDir="column">
                  <Text>
                    <b>Valor total dos itens:</b> {item.valor_total_itens}
                  </Text>
                  <Text>
                    <b>Preço do frete:</b> R$ 0
                  </Text>
                  <Text>
                    <b>Valor total:</b> {item.valor_total_itens}
                  </Text>
                </Flex>
              </Grid>
            </Box>

            <Flex maxW="959px" w="100%" flexDir="column" gap={4}>
              <Flex flexDir="column" gap={4}>
                <Flex flexDir="column">
                  <Text fontSize="15px" bg="#378BBF" py={1} textAlign="center" color="white">
                    Solicitar Novo Layout
                  </Text>
                  <Textarea
                    borderRadius={0}
                    minH="120px"
                    resize="none"
                    textAlign="center"
                    placeholder="Espaço reservado para você enviar alguma informação adicional em relação ao Layout"
                    {...register('message')}
                    isInvalid={!!errors.message}
                    sx={{
                      '&::placeholder': {
                        color: '#2D2D2D',
                        fontSize: '12px',
                        opacity: '1',
                      },
                    }}
                  />

                  {errors.message && (
                    <Text color="red.500" fontSize="12px">
                      {errors.message.message}
                    </Text>
                  )}
                </Flex>
                <Flex justifyContent="center" alignItems="center" gap={2}>
                  <InputGroup>
                    <InputLeftAddon children="Link da logo" bgColor="#378BBF" color="white" fontSize="15px" fontWeight="semibold" borderRadius={0} />
                    <Input type="text" placeholder="Link" {...register('link')} isInvalid={!!errors.link} borderRadius={0} />
                  </InputGroup>
                  {errors.link && (
                    <Text color="red.500" fontSize="12px">
                      {errors.link.message}
                    </Text>
                  )}

                  <Flex flexDir="column" gap={2}>
                    <Checkbox {...register('vetorizacao')}>Vetorização</Checkbox>
                    <Checkbox 
                      {...register('repeticao')} 
                      isDisabled={repeticaoMarcada}
                    >
                      Repetição
                    </Checkbox>
                  </Flex>
                </Flex>

                {repeticao && (
                  <Flex flexDir="column" gap={2}>
                    <Text fontSize="14px" fontWeight="semibold">Pedido de repetição:</Text>
                    <Input
                      type="text"
                      placeholder="Digite apenas números"
                      {...register('pedido_repeticao')}
                      isInvalid={!!errors.pedido_repeticao}
                      onKeyPress={(e) => {
                        // Permite apenas números
                        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                          e.preventDefault()
                        }
                      }}
                      maxLength={20}
                    />
                    {errors.pedido_repeticao && (
                      <Text color="red.500" fontSize="12px">
                        {errors.pedido_repeticao.message}
                      </Text>
                    )}
                  </Flex>
                )}
              </Flex>
            </Flex>
          </DrawerBody>

          <DrawerFooter position="sticky" mt="auto" bottom="0" boxShadow="0px 3px 6px rgba(0, 0, 0, 0.635)">
            <Flex alignItems="center" justifyContent="center" w="100%" gap={2}>
              <Button
                maxW="181px"
                w="100%"
                fontSize="15px"
                fontWeight="semibold"
                bgColor="transparent"
                color="#FFA200"
                border="1px solid #FFA200"
                onClick={handleClose}
              >
                <AiOutlineArrowLeft style={{ marginRight: '5px' }} />
                Voltar
              </Button>

              <Button
                fontSize="15px"
                type="submit"
                isLoading={isLoading}
                loadingText="Enviando..."
                maxW="181px"
                w="100%"
                isDisabled={isSubmitting || isLoading | item.trava_item}
                onClick={handleSubmit(onSubmit)}
              >
                Solicitar Layout
              </Button>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}

// export const ButtonUpload = forwardRef(({ control, ...props }, ref) => {
//   return (
//     <Controller
//       control={control}
//       name="upload"
//       render={({ field }) => (
//         <>
//           <Button
//             cursor="pointer"
//             as="label"
//             htmlFor="upload"
//             fontSize="15px"
//             bgColor="white"
//             color="#2D2D2D"
//             leftIcon={<AiOutlineUpload size="20px" color="#378BBF" />}
//             border="1px solid"
//             borderColor={props.isInvalid ? 'red.500' : '#378BBF'}
//             {...props}>
//             <Tooltip label={props.label} bg="#378BBF" color="white" fontSize="md" placement="top">
//               <Text as="span" maxW="100px" isTruncated>
//                 {props.label}
//               </Text>
//             </Tooltip>
//           </Button>
//           <input
//             id="upload"
//             type="file"
//             style={{ display: 'none' }}
//             name="upload"
//             onChange={(e) =>
//               field.onChange({
//                 target: { value: e.target.files[0], name: field.name }
//               })
//             }
//           />
//         </>
//       )}
//       {...props}
//     />
//   );
// });
